/**
 * useWaInboxRealtime — canal Supabase Realtime del inbox WhatsApp.
 *
 * Centraliza los 2 canales que WhatsAppInboxPage necesita en todo momento:
 *   1. postgres_changes sobre wa_conversations + wa_messages (tenant-scoped).
 *   2. broadcast de presencia (composing/recording/available desde Evolution API).
 *
 * El canal de agent-typing permanece en la página porque varía con selectedConvId
 * y es menos crítico.
 *
 * Todos los setters y refs reciben sus valores del caller — el hook NO posee estado.
 */

import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { playReceiveSound } from '@/lib/whatsappSounds';
import { alReintentarElChat, alReintentarElMensaje, bestDeliveryStatus, convMatchesFilter, esHistorico, isRenderableMessage } from '@/lib/wa-utils';
import { waDisplayName } from '@/components/whatsapp/wa-helpers';
import { elTextoDelAviso, laEtiquetaDelAviso, type EtiquetaDelCelular } from '@/lib/whatsapp/elAvisoDeLaEtiqueta';
import type { WaConversation } from '@/hooks/useWaConversations';
import type { WaMessage } from '@/components/whatsapp/WaMessageThread';
import { aplicarAlHilo, bajarScrollDelHilo, hayHiloAbierto } from '@/hooks/waThreadRegistry';
import { conCitas } from '@/hooks/useWaMessages';
import { reconciliarLoAbierto, CADENCIA_MS } from '@/lib/whatsapp/reconciliacionDelChat';
import { medirLlegadaDeMensaje, medirEstadoDelCanal, estadoDesdeSupabase } from '@/lib/whatsapp/medicionDelChat';
import { marcarVistoEnWhatsApp } from '@/lib/whatsapp/elVistoDelChat';
import { lasEtiquetasEstanEnVuelo } from '@/lib/whatsapp/cambiarLasEtiquetas'; // 20-oct · lo elegido no se pisa

type Conversation = WaConversation;
type Message = WaMessage;

interface Options {
  tenantId: string | null;
  statusFilterRef: React.MutableRefObject<string>;
  loadConversationsRef: React.MutableRefObject<(() => Promise<void>) | null>;
  conversationsRef: React.MutableRefObject<WaConversation[]>;
  setConversations: React.Dispatch<React.SetStateAction<WaConversation[]>>;
  setPresenceMap: React.Dispatch<React.SetStateAction<Map<string, 'composing' | 'recording' | 'available'>>>;
  /**
   * La conversación desapareció de la vista (la borraron, o un cambio de estado la sacó del filtro
   * activo). Solo le importa a quien tenga una seleccionada — la bandeja.
   */
  onConversacionFuera?: (convId: string) => void;
  /**
   * Aviso de mensaje entrante en una conversación que NO está abierta en pantalla. Opcional: si
   * nadie lo registra, no se avisa. El aviso es una decisión de la pantalla, no del canal.
   */
  showToast?: (content: import('./useInboxToast').ToastContent) => void;
  /**
   * Quién está mirando. Sólo se usa para medir: `admin_rum_events` exige tenant y usuario, y sin
   * los dos la fila se descarta. Opcional a propósito — el canal tiene que funcionar igual sin
   * telemetría.
   */
  userId?: string | null;
}

export function useWaInboxRealtime({
  tenantId,
  statusFilterRef,
  loadConversationsRef,
  conversationsRef,
  setConversations,
  setPresenceMap,
  onConversacionFuera,
  showToast,
  userId,
}: Options): void {
  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════════
   * 🚨 «SUENA A CADA RATO Y NO HAY NADA» — el founder, 28-ago-2026.
   * ═══════════════════════════════════════════════════════════════════════════════════════════
   * Y en el mismo rato: «avisé a un cliente que llegó su pedido y me salió un mensaje repetido de
   * Alvaro que decía quiero el duromax». Son EL MISMO defecto, visto de dos maneras.
   *
   * Cada mensaje entrante llega por DOS caminos a esta pantalla: el `postgres_changes` de la tabla
   * y el `broadcast` que publica el webhook para que se vea al instante. Con el hilo abierto no se
   * nota, porque ahí el sonido va después del dedupe de la lista. Pero con el hilo CERRADO —que es
   * el caso normal, uno mira otra conversación— las dos ramas hacían `playReceiveSound()` y
   * `showToast()` sin preguntar si ese mensaje ya se había avisado.
   *
   * Resultado: el sonido suena dos veces por mensaje, y cualquier reenvío del canal —una
   * reconexión, un evento repetido— vuelve a sonar y a mostrar el globo de un mensaje viejo. Que
   * es exactamente «apareció de la nada un mensaje de Alvaro»: no apareció, se REPITIÓ el aviso de
   * uno que ya había llegado.
   *
   * 🪤 EL RECUERDO VIVE EN UN `ref`, NO EN ESTADO. Guardarlo en `useState` volvería a renderizar la
   * bandeja entera en cada mensaje, y encima el efecto se re-suscribiría. Y vive FUERA del
   * `useEffect` a propósito: adentro se vaciaría en cada reconexión del canal, que es justo cuando
   * llegan los eventos repetidos que esto viene a filtrar.
   *
   * 🪤 Y SE PODA. Sin tope, una jornada de trabajo deja miles de ids en memoria. Se conservan los
   * últimos: un evento repetido llega en segundos, no en horas.
   */
  const yaAvisados = useRef<Set<string>>(new Set());
  const seAvisaUnaSolaVez = useCallback((claveDelMensaje: string | null | undefined): boolean => {
    if (!claveDelMensaje) return true; // sin identidad no se puede deduplicar: mejor avisar de más
    if (yaAvisados.current.has(claveDelMensaje)) return false;
    yaAvisados.current.add(claveDelMensaje);
    if (yaAvisados.current.size > 600) {
      // Se descartan los más viejos conservando el orden de inserción del Set.
      yaAvisados.current = new Set([...yaAvisados.current].slice(-300));
    }
    return true;
  }, []);

  /*
   * 🪤 LA IDENTIDAD SE LEE POR REF Y NO ENTRA EN LAS DEPS DEL EFECTO. Meter `userId` en las
   * dependencias re-suscribiría el canal entero cada vez que la sesión se refresca — una
   * reconexión gratis por hora, que es justo el defecto que esto viene a medir.
   */
  const identidadRef = useRef({ tenant_id: tenantId, user_id: userId ?? null });
  identidadRef.current = { tenant_id: tenantId, user_id: userId ?? null };

  useEffect(() => {
    if (!tenantId) return;

    /* Cuándo nació este canal: sirve para saber cuánto vivió antes de romperse. */
    const nacimiento = Date.now();

    /* 19-oct · Las etiquetas que cambian en el celular se avisan de a tandas: varias seguidas son UN aviso. */
    let etiquetasDelCelular: EtiquetaDelCelular[] = [];
    let tandaDeEtiquetas: ReturnType<typeof setTimeout> | null = null;
    const avisarLasEtiquetas = () => {
      const aviso = elTextoDelAviso(etiquetasDelCelular);
      etiquetasDelCelular = [];
      if (!aviso) return;
      const conv = conversationsRef.current.find((c) => c.id === aviso.conversationId);
      const cj = conv?.contact_jid ?? null;
      showToast?.({
        conversationId: aviso.conversationId,
        name: conv ? waDisplayName(conv.contact_name, conv.contact_phone, cj) : 'Chat',
        body: aviso.texto,
        phone: conv?.contact_phone ?? '',
        photoUrl: (conv as { contact_photo_url?: string | null } | undefined)?.contact_photo_url ?? null,
        contactJid: cj,
      });
    };

    const channel = supabase
      .channel(`wa-inbox-${tenantId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'wa_conversations', filter: `tenant_id=eq.${tenantId}` },
        (payload) => {
          // Inline broadcast: usar payload.new directo en lugar de refetch
          // completo. Ahorra 1 roundtrip + 200ms en cada conversación nueva.
          // Solo caemos a refetch si filter activo descarta este status
          // (no tiene sentido mostrar lo que el filter oculta).
          const u = payload.new as Record<string, unknown>;
          const id = u.id as string;
          const status = (u.status as string) ?? 'pending';
          if (!convMatchesFilter(status, statusFilterRef.current)) return;

          setConversations((prev) => {
            // Idempotente: si ya existe (race con realtime + refetch inicial), skip.
            if (prev.some((c) => c.id === id)) return prev;
            const newConv = {
              ...u,
              tags: Array.isArray(u.tags) ? u.tags : [],
              unread_count: (u.unread_count as number) ?? 0,
              message_count: (u.message_count as number) ?? 0,
            } as unknown as Conversation;
            // Bump al top: la conv recién creada va primera por last_message_at.
            let result = [newConv, ...prev].sort((a, b) => {
              const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
              const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
              return tb - ta;
            });
            // 🔧 Deduplicar por si acaso: realtime puede emitir INSERT fantasma
            const seen = new Set<string>();
            result = result.filter((c) => {
              if (seen.has(c.id)) return false;
              seen.add(c.id);
              return true;
            });
            return result;
          });
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'wa_conversations', filter: `tenant_id=eq.${tenantId}` },
        (payload) => {
          const u = payload.new as Record<string, unknown>;
          const id = u.id as string;
          setConversations((prev) => {
            const exists = prev.some((c) => c.id === id);
            if (!exists) { void loadConversationsRef.current?.(); return prev; }

            const newStatus = u.status as string | undefined;
            if (newStatus && !convMatchesFilter(newStatus, statusFilterRef.current)) {
              onConversacionFuera?.(id);
              return prev.filter((c) => c.id !== id);
            }

            const patched = prev.map((c) => {
              if (c.id !== id) return c;
              return {
                ...c,
                ...(u.contact_name !== undefined && { contact_name: u.contact_name as string | null }),
                ...(u.contact_photo_url !== undefined && { contact_photo_url: u.contact_photo_url as string | null }),
                ...(u.status !== undefined && { status: u.status as string }),
                ...(u.assigned_agent_id !== undefined && { assigned_agent_id: u.assigned_agent_id as string | null }),
                ...(u.team_id !== undefined && { team_id: u.team_id as string | null }),
                ...(u.priority !== undefined && { priority: u.priority as string | null }),
                ...(u.ai_handling !== undefined && { ai_handling: u.ai_handling as boolean }),
                ...(u.unread_count !== undefined && { unread_count: u.unread_count as number }),
                ...(u.message_count !== undefined && { message_count: u.message_count as number }),
                ...(u.last_message_at !== undefined && { last_message_at: u.last_message_at as string | null }),
                ...(u.last_message_body !== undefined && { last_message_body: u.last_message_body as string | null }),
                ...(u.last_message_type !== undefined && { last_message_type: u.last_message_type as string | null }),
                ...(u.last_message_direction !== undefined && { last_message_direction: u.last_message_direction as string | null }),
                // Anti-degradación: si ya teníamos un status superior (read)
                // y llega UPDATE con uno inferior (delivered) por race del
                // realtime out-of-order, conservamos el mayor. Mismo guard
                // que el backend processStatusUpdate, replicado en cliente.
                ...(u.last_message_status !== undefined && {
                  last_message_status: bestDeliveryStatus(
                    c.last_message_status ?? '',
                    (u.last_message_status as string | null) ?? '',
                  ) || (u.last_message_status as string | null),
                }),
                // 20-oct · Con cambios de etiquetas en vuelo, manda la pantalla: el tiempo real de un guardado anterior
                // pisaba lo recién elegido (lib/whatsapp/cambiarLasEtiquetas.ts).
                ...(u.tags !== undefined && !lasEtiquetasEstanEnVuelo(c.id) && { tags: (u.tags as string[]) ?? c.tags }),
                ...(u.is_pinned !== undefined && { is_pinned: u.is_pinned as boolean }),
                ...(u.muted_until !== undefined && { muted_until: u.muted_until as string | null }),
                ...(u.snoozed_until !== undefined && { snoozed_until: u.snoozed_until as string | null }),
                ...(u.crm_lead_id !== undefined && { crm_lead_id: u.crm_lead_id as string | null }),
                ...(u.sla_first_response_breached !== undefined && { sla_first_response_breached: u.sla_first_response_breached as boolean }),
                ...(u.sla_resolution_breached !== undefined && { sla_resolution_breached: u.sla_resolution_breached as boolean }),
              } as Conversation;
            });
            // Re-ordenar si el UPDATE cambia last_message_at. El RPC
            // wa_send_prepare hace UPDATE wa_conversations + INSERT
            // wa_messages; si el UPDATE llega primero y no reordenamos,
            // la conv queda con texto nuevo pero en posición vieja
            // hasta el INSERT (race visible en chats activos).
            let result = patched;
            if (u.last_message_at !== undefined) {
              result = [...patched].sort((a, b) => {
                const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
                const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
                return tb - ta;
              });
            }
            // 🔧 Deduplicar por si acaso: realtime UPDATE puede causar fantasmas
            const seen = new Set<string>();
            result = result.filter((c) => {
              if (seen.has(c.id)) return false;
              seen.add(c.id);
              return true;
            });
            return result;
          });
        },
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'wa_conversations', filter: `tenant_id=eq.${tenantId}` },
        (payload) => {
          // Inline: filtrar por id en lugar de refetch.
          const id = (payload.old as { id?: string })?.id;
          if (!id) return;
          setConversations((prev) => prev.filter((c) => c.id !== id));
          onConversacionFuera?.(id);
        },
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'wa_messages', filter: `tenant_id=eq.${tenantId}` },
        (payload) => {
          const rawMsg = payload.new as Record<string, unknown> & { conversation_id: string };
          const msg = {
            ...rawMsg,
            reactions: Array.isArray(rawMsg.reactions) ? rawMsg.reactions : [],
            wa_message_id: (rawMsg.wa_message_id as string) ?? null,
          } as Message & { conversation_id: string };

          // Fantasmas (reactions coladas como mensaje, bubbles vacíos):
          // ignorar todo el INSERT — no entran al thread ni patchean
          // last_message_*/unread de la conv. WA Web nunca los muestra.
          if (!isRenderableMessage(msg)) return;

          /*
           * 🚨 21-oct · UN MENSAJE TRAÍDO NO ES UNO QUE LLEGA. Al importar historial (Chatwoot, Evolution) entran
           * miles de filas viejas de golpe: ninguna suena, avisa, sube su conversación ni cuenta como no leída.
           * Los triggers de la base ya salen con la misma familia (fn_es_historico, 20261021231500); acá es la
           * pantalla la que no tiene que enterarse. El hilo las muestra cuando se abre, como cualquier historial.
           */
          if (esHistorico(msg.origen)) return;

          /*
           * F0: por acá entran los mensajes que trae la replicación de Postgres — el camino LENTO,
           * el que paga una evaluación de RLS por cada suscriptor. Si el p95 de `/chat/replicacion`
           * es mucho peor que el de `/chat/broadcast`, ése es el número que justifica F3.
           */
          if (msg.direction === 'inbound') {
            medirLlegadaDeMensaje('replicacion', msg.created_at, identidadRef.current);

            /*
             * ── EL ✓✓ AZUL DE UN MENSAJE QUE LLEGA CON EL CHAT YA ABIERTO ──────────────────
             * 🪤 EL VISTO SE DISPARABA SÓLO AL ABRIR LA CONVERSACIÓN. Vive dentro del cargador
             * del hilo (`useWaMessages`), que corre al abrir o al cambiar de chat. Un mensaje
             * que entra mientras el operador YA está mirando esa conversación no pasaba por
             * ahí, y nada lo marcaba: el cliente veía sus mensajes en gris para siempre
             * aunque del otro lado los estuvieran leyendo en vivo.
             *
             * Se ve exactamente así: abrís el chat, se marca todo lo viejo, y de ahí en
             * adelante ninguno más. Reportado el 2026-09-07 sobre la línea de Cloud API.
             *
             * 🪤 SÓLO SI EL HILO ESTÁ ABIERTO. Marcar al llegar, sin que nadie esté mirando,
             * sería mentirle al cliente: el ✓✓ azul significa «alguien lo leyó», no «el
             * servidor lo recibió». Esa distinción es lo único que hace útil al tilde.
             */
            if (hayHiloAbierto(msg.conversation_id)) {
              void marcarVistoEnWhatsApp(msg.conversation_id);
            }
          }

          if (hayHiloAbierto(msg.conversation_id)) {
            aplicarAlHilo(msg.conversation_id, (prev) => {
              if (prev.some((m) => m.id === msg.id || (msg.wa_message_id && m.wa_message_id === msg.wa_message_id))) {
                return prev;
              }
              if (msg.direction === 'outbound') {
                // matchTemp dedupea optimistic temp message contra realtime
                // INSERT del mismo envío. 3 estrategias en orden:
                // 1. body match (texto puro)
                // 2. media_url match (storage URL después del upload)
                // 3. msg_type + recent (~10s) match para media sin caption
                //    cuyos URLs no coinciden (blob://temp vs https://storage)
                const RECENT_MS = 10_000;
                const msgCreatedAt = new Date(msg.created_at).getTime();
                const matchTemp = (m: Message) => {
                  if (!m.id.startsWith('temp-')) return false;
                  /*
                   * Strategy 0 (21-oct): la MISMA clave con la que se mandó. Si las dos la tienen, decide sólo ella:
                   * con varias fotos en vuelo, «mismo tipo y reciente» le daba la fila de la segunda a la burbuja de
                   * otra (por ejemplo, a una cuya subida falló).
                   */
                  if (m.client_idempotency_key && msg.client_idempotency_key) return m.client_idempotency_key === msg.client_idempotency_key;
                  // Strategy 1: body match (texto)
                  if (m.body && m.body === msg.body) return true;
                  // Strategy 2: media_url exact match
                  if (m.media_url && m.media_url === msg.media_url) return true;
                  // Strategy 3: misma msg_type media sin caption + creado dentro
                  // de 10s — cubre el race donde realtime llega ANTES de que el
                  // upload local complete su URL pública (blob:// vs https://).
                  if (m.msg_type === msg.msg_type
                      && msg.msg_type !== 'text'
                      && !m.body && !msg.body) {
                    const tempCreatedAt = new Date(m.created_at).getTime();
                    if (Math.abs(msgCreatedAt - tempCreatedAt) < RECENT_MS) return true;
                  }
                  return false;
                };
                const hasTemp = prev.some(matchTemp);
                if (hasTemp) {
                  let replaced = false;
                  return prev.map((m) => {
                    if (!replaced && matchTemp(m)) {
                      replaced = true;
                      const bestStatus = bestDeliveryStatus(m.delivery_status, msg.delivery_status);
                      return {
                        ...msg,
                        delivery_status: bestStatus,
                        ...(m.quoted_message ? { quoted_message: m.quoted_message, quoted_message_id: m.quoted_message_id } : {}),
                      };
                    }
                    return m;
                  });
                }
              }
              if (msg.direction === 'inbound' && seAvisaUnaSolaVez(msg.wa_message_id ?? msg.id)) playReceiveSound();
              // 🚨 La cita se arma acá también (18-sep): el mensaje llegaba crudo y «🛍 Waffle Clásico» sólo aparecía al refrescar.
              return conCitas([...prev, msg]);
            });
            bajarScrollDelHilo(msg.conversation_id);
          } else if (msg.direction === 'inbound' && seAvisaUnaSolaVez(msg.wa_message_id ?? msg.id)) {
            // 🪤 El aviso pasa por el mismo portón que el sonido: un globo repetido de un mensaje
            // viejo se lee como un mensaje nuevo, que fue justo lo que reportó el founder.
            playReceiveSound();
            const conv = conversationsRef.current.find((c: Conversation) => c.id === rawMsg.conversation_id);
            const cj = (conv as { contact_jid?: string | null } | undefined)?.contact_jid ?? null;
            showToast?.({
              conversationId: rawMsg.conversation_id,
              name: conv ? waDisplayName(conv.contact_name, conv.contact_phone, cj) : 'Nuevo mensaje',
              body: (rawMsg.body as string)?.slice(0, 80) || '📎 Archivo',
              phone: conv?.contact_phone ?? '',
              photoUrl: (conv as { contact_photo_url?: string | null } | undefined)?.contact_photo_url ?? null,
              contactJid: cj,
            });
          }

          setConversations((prev) => {
            // Patch + bump al top: la conversation que recibió el mensaje
            // se re-ordena para quedar primera (patrón WhatsApp Web/Slack).
            // Sin esto la lista solo se reordenaba al refrescar la página.
            const updated = prev.map((c) => {
              if (c.id !== rawMsg.conversation_id) return c;
              return {
                ...c,
                last_message_body: (rawMsg.body as string) ?? null,
                last_message_type: (rawMsg.msg_type as string) ?? 'text',
                last_message_direction: (rawMsg.direction as string) ?? 'inbound',
                last_message_status: (rawMsg.delivery_status as string) ?? 'sent',
                last_message_at: (rawMsg.created_at as string) ?? new Date().toISOString(),
                // Si el hilo está en pantalla (bandeja o ventana acoplada) el mensaje ya se está viendo.
                unread_count: hayHiloAbierto(rawMsg.conversation_id) ? c.unread_count : c.unread_count + 1,
                typing: false,
              };
            });
            return [...updated].sort((a, b) => {
              const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
              const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
              return tb - ta;
            });
          });
        },
      )
      .on(
        'broadcast',
        { event: 'wa_message_insert' },
        (payload) => {
          // Server-side broadcast: el webhook publica el mensaje inbound al
          // canal Realtime ANTES de que postgres logical replication lo
          // empuje vía postgres_changes. Llega ~100-200ms antes.
          //
          // Dedup: el INSERT handler de postgres_changes va a llegar después
          // con el mismo mensaje. Filtramos por id en setMessages.
          const rawMsg = payload.payload as Record<string, unknown> & { conversation_id: string };
          if (!rawMsg?.id) return;
          const msg = {
            ...rawMsg,
            reactions: Array.isArray(rawMsg.reactions) ? rawMsg.reactions : [],
            wa_message_id: (rawMsg.wa_message_id as string) ?? null,
          } as Message & { conversation_id: string };

          // Mismo guard de fantasmas que el INSERT postgres_changes.
          if (!isRenderableMessage(msg)) return;

          /*
           * F0: el aviso directo del webhook — el camino RÁPIDO. Se mide antes del dedupe a
           * propósito: interesa cuándo LLEGÓ el aviso, no si se terminó dibujando. Los dos caminos
           * traen el mismo mensaje, y comparar sus percentiles es lo que dice cuánto vale cada uno.
           */
          if (msg.direction === 'inbound') {
            medirLlegadaDeMensaje('broadcast', msg.created_at, identidadRef.current);
          }

          if (hayHiloAbierto(msg.conversation_id)) {
            aplicarAlHilo(msg.conversation_id, (prev) => {
              if (prev.some((m) => m.id === msg.id || (msg.wa_message_id && m.wa_message_id === msg.wa_message_id))) {
                return prev;
              }
              if (msg.direction === 'inbound' && seAvisaUnaSolaVez(msg.wa_message_id ?? msg.id)) playReceiveSound();
              return [...prev, msg];
            });
            bajarScrollDelHilo(msg.conversation_id);
          } else if (msg.direction === 'inbound' && seAvisaUnaSolaVez(msg.wa_message_id ?? msg.id)) {
            // 🪤 El aviso pasa por el mismo portón que el sonido: un globo repetido de un mensaje
            // viejo se lee como un mensaje nuevo, que fue justo lo que reportó el founder.
            playReceiveSound();
            const conv = conversationsRef.current.find((c: Conversation) => c.id === rawMsg.conversation_id);
            const cj = (conv as { contact_jid?: string | null } | undefined)?.contact_jid ?? null;
            showToast?.({
              conversationId: rawMsg.conversation_id,
              name: conv ? waDisplayName(conv.contact_name, conv.contact_phone, cj) : 'Nuevo mensaje',
              body: (rawMsg.body as string)?.slice(0, 80) || '📎 Archivo',
              phone: conv?.contact_phone ?? '',
              photoUrl: (conv as { contact_photo_url?: string | null } | undefined)?.contact_photo_url ?? null,
              contactJid: cj,
            });
          }

          // Bump conversation al top en la lista
          setConversations((prev) => {
            const updated = prev.map((c) => {
              if (c.id !== rawMsg.conversation_id) return c;
              return {
                ...c,
                last_message_body: (rawMsg.body as string) ?? null,
                last_message_type: (rawMsg.msg_type as string) ?? 'text',
                last_message_direction: (rawMsg.direction as string) ?? 'inbound',
                last_message_status: (rawMsg.delivery_status as string) ?? 'delivered',
                last_message_at: (rawMsg.created_at as string) ?? new Date().toISOString(),
                // Si el hilo está en pantalla (bandeja o ventana acoplada) el mensaje ya se está viendo.
                unread_count: hayHiloAbierto(rawMsg.conversation_id) ? c.unread_count : c.unread_count + 1,
                typing: false,
              };
            });
            return [...updated].sort((a, b) => {
              const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
              const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
              return tb - ta;
            });
          });
        },
      )
      .on(
        'broadcast',
        { event: 'wa_message_status' },
        (payload) => {
          const p = payload.payload as { message_id: string; conversation_id: string; delivery_status: string; is_latest_in_conv: boolean };
          if (!p?.message_id) return;

          if (hayHiloAbierto(p.conversation_id)) {
            aplicarAlHilo(p.conversation_id, (prev) => prev.map((m) => {
              if (m.id !== p.message_id) return m;
              return { ...m, delivery_status: bestDeliveryStatus(m.delivery_status, p.delivery_status) };
            }));
          }

          if (p.is_latest_in_conv) {
            setConversations((prev) => prev.map((c) => {
              if (c.id !== p.conversation_id || c.last_message_direction !== 'outbound') return c;
              const next = bestDeliveryStatus(c.last_message_status ?? '', p.delivery_status);
              if (next !== c.last_message_status) return { ...c, last_message_status: next };
              return c;
            }));
          }
        },
      )
      .on(
        'broadcast',
        { event: 'wa_etiqueta_del_celular' },
        (payload) => {
          // 19-oct · Una etiqueta cambió DESDE EL CELULAR (el servidor no avisa el eco de lo que puso Smarty): toast.
          const e = laEtiquetaDelAviso(payload.payload);
          if (!e) return;
          etiquetasDelCelular.push(e);
          if (tandaDeEtiquetas) clearTimeout(tandaDeEtiquetas);
          tandaDeEtiquetas = setTimeout(avisarLasEtiquetas, 800);
        },
      )
      .on(
        'broadcast',
        { event: 'wa_message_retry' },
        (payload) => {
          /*
           * 18-oct · Un fallido se reenvía EN SU FILA (`reintentarElFallido.ts`). «Fallido» es el rango más alto y
           * `bestDeliveryStatus` nunca lo baja —a propósito: Meta acepta y después avisa que falló—, así que el ✓ del
           * reenvío no lo movería. Este aviso es la única puerta de vuelta: lo pone en «enviando…» y desde ahí avanza
           * como cualquier envío. Sólo lo manda el servidor cuando de verdad toma el mensaje para un intento nuevo.
           */
          const p = payload.payload as { message_id: string; conversation_id: string; is_latest_in_conv: boolean };
          if (!p?.message_id) return;
          if (hayHiloAbierto(p.conversation_id)) aplicarAlHilo(p.conversation_id, (prev) => alReintentarElMensaje(prev, p.message_id));
          if (p.is_latest_in_conv) setConversations((prev) => alReintentarElChat(prev, p.conversation_id));
        },
      )
      .on(
        'broadcast',
        { event: 'wa_message_reaction' },
        (payload) => {
          // INSTANT broadcast de emoji reactions. Mismo dedup que status:
          // el postgres_changes UPDATE llegará después con el mismo reactions[]
          // y el merge será idempotente.
          const p = payload.payload as { message_id: string; conversation_id: string; reactions: Array<{ emoji: string; from: string; from_me: boolean; timestamp: number }> };
          if (!p?.message_id) return;
          aplicarAlHilo(p.conversation_id, (prev) => prev.map((m) => {
            if (m.id !== p.message_id) return m;
            return { ...m, reactions: p.reactions };
          }));
        },
      )
      .on(
        'broadcast',
        { event: 'wa_message_edit' },
        (payload) => {
          // INSTANT broadcast de edit inbound (Feature 3 Fase B).
          // Cuando el contacto edita un mensaje, el webhook hace UPDATE
          // del wa_messages.body + is_edited + edited_at, y broadcast
          // este evento para que el frontend refleje el cambio sin
          // esperar postgres_changes (~50ms vs ~300ms).
          const p = payload.payload as { message_id: string; conversation_id: string; new_body: string; edited_at: string };
          if (!p?.message_id) return;
          aplicarAlHilo(p.conversation_id, (prev) => prev.map((m) => {
            if (m.id !== p.message_id) return m;
            return {
              ...m,
              body: p.new_body,
              is_edited: true,
              edited_at: p.edited_at,
            } as typeof m;
          }));
        },
      )
      .on(
        'broadcast',
        { event: 'wa_message_delete' },
        (payload) => {
          // INSTANT broadcast de revoke ("Eliminar para todos") inbound.
          // El webhook setea deleted_at; el bubble renderiza el estado
          // "🚫 Este mensaje fue eliminado" sin esperar postgres_changes.
          const p = payload.payload as { message_id: string; conversation_id: string; deleted_at: string };
          if (!p?.message_id) return;
          aplicarAlHilo(p.conversation_id, (prev) => prev.map((m) => {
            if (m.id !== p.message_id) return m;
            return { ...m, deleted_at: p.deleted_at } as typeof m;
          }));
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'wa_messages', filter: `tenant_id=eq.${tenantId}` },
        (payload) => {
          const raw = payload.new as Record<string, unknown> & { conversation_id: string };
          const updated = {
            ...raw,
            reactions: Array.isArray(raw.reactions) ? raw.reactions : [],
            wa_message_id: (raw.wa_message_id as string) ?? null,
          } as Message & { conversation_id: string };

          if (hayHiloAbierto(updated.conversation_id)) {
            aplicarAlHilo(updated.conversation_id, (prev) => prev.map((m) => {
              if (m.id !== updated.id) return m;
              return {
                ...m,
                delivery_status: bestDeliveryStatus(m.delivery_status, updated.delivery_status),
                reactions: updated.reactions,
                media_url: updated.media_url ?? m.media_url,
                // media_status y media_thumbnail también se propagan: el
                // background download del webhook actualiza el row con
                // media_url + media_status='in_r2'. Sin propagar status,
                // el frontend seguía viendo 'pending' aunque ya tuviera URL.
                ...(updated.media_status !== undefined ? { media_status: updated.media_status } : {}),
                ...(updated.media_thumbnail !== undefined ? { media_thumbnail: updated.media_thumbnail } : {}),
                // Feature 5c-B: el pre-fetch eager de orderDetails actualiza
                // order_info.items DESPUÉS del INSERT. Sin propagar acá, la
                // card no muestra la imagen del producto hasta refrescar.
                ...(updated.order_info !== undefined ? { order_info: updated.order_info } : {}),
                // Revoke "Eliminar para todos": propagación postgres_changes
                // como fallback del broadcast wa_message_delete.
                ...(updated.deleted_at !== undefined ? { deleted_at: updated.deleted_at as string | null } : {}),
              };
            }));
          }

          if (updated.direction === 'outbound') {
            setConversations((prev) => prev.map((c) => {
              if (c.id !== updated.conversation_id || c.last_message_direction !== 'outbound') return c;
              const next = bestDeliveryStatus(c.last_message_status ?? '', updated.delivery_status);
              if (next !== c.last_message_status) return { ...c, last_message_status: next };
              return c;
            }));
          }
        },
      )
      /*
       * ═══════════════════════════════════════════════════════════════════════════════════════
       * 🚨 ACÁ NO HABÍA NADIE MIRANDO, Y ES EL DEFECTO QUE EXPLICA «no llegó y al refrescar sí».
       * ═══════════════════════════════════════════════════════════════════════════════════════
       * `.subscribe()` iba sin callback — igual que en los otros 21 lugares del módulo que abren un
       * canal. Sin callback, un canal muerto es indistinguible de uno sano: la pantalla sigue
       * pareciendo conectada, no llega nada, y el operador se entera recargando.
       *
       * 🪤 ESTO NO RECONECTA NI REPARA, Y ES A PROPÓSITO. Es F0: primero se ve, después se cura.
       * supabase-js ya trae su propia reconexión; agregarle otra acá, sin datos, sería adivinar.
       * Lo único que cambia hoy es que la caída deja rastro en vez de ser invisible.
       */
      .subscribe((status) => {
        medirEstadoDelCanal(estadoDesdeSupabase(status), nacimiento, identidadRef.current);
      });

    /*
     * ⭐ LA RED DE SEGURIDAD, Y NADA MÁS QUE ESO.
     *
     * El canal de arriba es el que entrega los mensajes, y lo mantiene supabase-js con su propia
     * reconexión. Esto de acá no lo toca: cada tanto le pregunta al servidor si hay algo posterior a
     * lo que está en pantalla, para que un evento perdido no se convierta en un chat congelado.
     *
     * 🚨 NO AGREGAR ACÁ UN SEGUNDO RECONECTOR. Ya se intentó —vigilar el canal, recrearlo, ponerle
     * nombres nuevos— y las tres veces salió peor: dos reconectores compitiendo se matan el canal
     * entre ellos y el chat termina viviendo de esta red, con un minuto de retraso y sin sonido.
     *
     * 🪤 SÓLO CON LA PESTAÑA A LA VISTA. Nadie necesita mensajes frescos en una pestaña que nadie
     * mira, y gastarlos ahí es batería y datos del operador. Al volver se revisa enseguida, que es
     * justo cuando el navegador pudo haber congelado el socket.
     */
    const revisar = async () => {
      if (document.visibilityState !== 'visible') return;
      const faltaban = await reconciliarLoAbierto(tenantId, identidadRef.current).catch(() => 0);
      // Si aparecieron mensajes que el canal no trajo, la lista también está atrasada.
      if (faltaban > 0) void loadConversationsRef.current?.();
    };
    const latido = setInterval(() => { void revisar(); }, CADENCIA_MS);
    const alVolverALaPestana = () => { void revisar(); };
    document.addEventListener('visibilitychange', alVolverALaPestana);
    window.addEventListener('online', alVolverALaPestana);

    // Presencia — modelo WhatsApp Web (NO timers de expiración para
    // 'available'): WhatsApp manda 'available' UNA vez al entrar en línea
    // y 'unavailable' al salir; entre medio NO repite nada. Por eso
    // 'available' es STICKY hasta que llegue 'unavailable' (expirarlo a
    // los 60s mostraba "últ. vez" falsa mientras el contacto seguía
    // online sin escribir). composing/recording son overlays transitorios
    // que, al terminar, vuelven a 'available' (sigue online), NO a offline.
    // Timestamp del último evento por teléfono (scope del effect, persiste
    // entre eventos mientras la subscripción vive). Sirve para el TTL de
    // seguridad reseteable: un timer viejo no actúa si llegó un evento
    // más nuevo (presenceTouch difiere).
    const presenceTouch = new Map<string, number>();
    // Muchos contactos en modo privacidad (@lid 2024+) NUNCA emiten
    // 'unavailable' (WhatsApp lo bloquea), solo 'composing'/'available'.
    // Sin un TTL, "en línea" se quedaría PEGADO para siempre. 150s:
    // generoso para no parpadear durante interacción normal, pero corta
    // la mentira eterna si hay silencio total (no sabemos si sigue online).
    const PRESENCE_SAFETY_MS = 150_000;
    const presenceChannel = supabase
      .channel(`wa-presence:${tenantId}`)
      .on('broadcast', { event: 'presence' }, (payload) => {
        const { phone, status: rawStatus, last_seen } = payload.payload as { phone: string; status: string; last_seen?: string };
        // 'paused' = dejó de escribir pero sigue online (defensivo; el
        // backend ya normaliza). Solo 'unavailable' = realmente offline.
        const status = rawStatus === 'paused' ? 'available' : rawStatus;
        const online = status === 'composing' || status === 'recording' || status === 'available';
        const touchTs = Date.now();
        if (online) presenceTouch.set(phone, touchTs);
        else presenceTouch.delete(phone);
        setPresenceMap((prev) => {
          const next = new Map(prev);
          if (online) next.set(phone, status as 'composing' | 'recording' | 'available');
          else next.delete(phone); // 'unavailable' → offline
          return next;
        });
        // TTL de seguridad: si NO llega ningún evento nuevo en 150s
        // (presenceTouch sigue siendo touchTs), limpiamos "en línea" —
        // no podemos afirmar que siga online (WhatsApp no manda
        // 'unavailable' para privacy-restringidos). Resetea con c/evento.
        if (online) {
          setTimeout(() => {
            if (presenceTouch.get(phone) !== touchTs) return; // llegó algo más nuevo
            presenceTouch.delete(phone);
            setPresenceMap((prev) => {
              if (!prev.has(phone)) return prev;
              const next = new Map(prev);
              next.delete(phone);
              return next;
            });
          }, PRESENCE_SAFETY_MS);
        }
        // "Última conexión" derivada (Fase 1): refleja el last_seen en la
        // conv en vivo, sin esperar refetch. El backend solo lo envía en
        // transiciones online/offline (no en el flicker de "escribiendo").
        // Guard no-op CRÍTICO: si nada cambió, devolvemos la MISMA ref del
        // array → React hace bail-out y NO se dispara la cascada de
        // efectos `conversations`-dependientes (parpadeo del header).
        if (last_seen) {
          setConversations((prev) => {
            let changed = false;
            const next = prev.map((c) => {
              if (c.contact_phone === phone && c.contact_last_seen_at !== last_seen) {
                changed = true;
                return { ...c, contact_last_seen_at: last_seen };
              }
              return c;
            });
            return changed ? next : prev;
          });
        }
        // SOLO composing/recording tienen fallback (8s): si no llega el
        // 'paused'/'available' que los cierra, NO borramos (eso marcaría
        // offline falsamente) → revertimos a 'available' (sigue online).
        // 'available' NO tiene timer: es sticky hasta 'unavailable'.
        if (status === 'composing' || status === 'recording') {
          setTimeout(() => {
            setPresenceMap((prev) => {
              if (prev.get(phone) !== status) return prev; // ya cambió
              const next = new Map(prev);
              next.set(phone, 'available'); // dejó de escribir, sigue online
              return next;
            });
          }, 8000);
        }
      })
      .subscribe((status) => {
        // Otra conexión distinta: la presencia se cae por su cuenta y hoy tampoco avisa.
        medirEstadoDelCanal(estadoDesdeSupabase(status), nacimiento, identidadRef.current);
      });

    return () => {
      if (tandaDeEtiquetas) clearTimeout(tandaDeEtiquetas);
      clearInterval(latido);
      document.removeEventListener('visibilitychange', alVolverALaPestana);
      window.removeEventListener('online', alVolverALaPestana);
      void supabase.removeChannel(channel);
      void supabase.removeChannel(presenceChannel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);
}
